/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { RichText, withDatasourceCheck } from "@sitecore-jss/sitecore-jss-nextjs";
import { extractRelativePath, extractRelativeURLPath } from "components/Shared/DomainSplit";
import { ComponentProps } from "lib/component-props";
import Image from "next/image";

type OpenDataPageContentProps = ComponentProps & {
  fields: OpenDataPageContentFields;
};

import {
  Field,
  TextField,
  RichTextField,
  LinkField,
  ImageField,
} from "@sitecore-jss/sitecore-jss-nextjs";
import Link from "next/link";
import { getDgAButtonClassNames } from "components/Shared/dga-button/DGAButton";
import { DGACTABox } from "components/Shared/dga-cta-box/DGACTABox";
import { DGAWarningBox } from "components/Shared/dga-warning-box/DGAWarningBox";

type fieldsPrinciplesItemsFieldsFields = {
  Description?: TextField | any;
  Icon?: ImageField;
  Title?: TextField | any;
};

type fieldsPrinciplesItemsFields = {
  id?: string;
  url?: string;
  name?: string;
  displayName?: string;
  fields?: fieldsPrinciplesItemsFieldsFields;
};

type OpenDataPageContentFields = {
  IntroTitle?: TextField | any;
  IntroDescription?: TextField | any;
  PrinciplesItems?: fieldsPrinciplesItemsFields[];
  Purpose?: RichTextField | any;
  PrinciplesTitle?: TextField | any;
  CTAButtonText?: TextField | any;
  CTAButtonUrl?: LinkField;
  Title?: TextField | any;
  WarningBoxLabel?: TextField | any;
  WarningBoxContent?: RichTextField | any;
  CTABoxButtonText?: TextField | any;
  CTABoxTitle?: TextField | any;
  CTABoxDescription?: RichTextField;
  CTABoxButtonLink?: LinkField;
  HideWarningBox?: Field<boolean>;
  HideCTABox?: Field<boolean>;
};

const OpenDataPageContent = (props: OpenDataPageContentProps): JSX.Element => {
  const {
    Title,
    IntroTitle,
    IntroDescription,
    PrinciplesItems,
    Purpose,
    PrinciplesTitle,
    CTAButtonText,
    CTAButtonUrl,
    WarningBoxLabel,
    WarningBoxContent,
    CTABoxTitle,
    CTABoxDescription,
    CTABoxButtonText,
    CTABoxButtonLink,
    HideWarningBox,
    HideCTABox,
  } = props?.fields || {};

  return (
    <section className="" aria-labelledby={Title?.value ? "open-data-title" : undefined}>
      {Title?.value && (
        <header className="bg-primary-50 pb-10">
          <h1 id="open-data-title" className="responsive font-bold text-3xl md:text-4xl md:font-semibold  ">
            {Title?.value}
          </h1>
        </header>
      )}

      {/* content container */}
      <div>
        {(IntroTitle?.value || IntroDescription?.value) && (
          <div className="responsive ">
            <div className=" flex flex-col gap-6 md:w-3/4 py-10">
              {IntroTitle?.value && (
                <h2 className="text-2xl font-semibold animate-top-dga ">{IntroTitle?.value}</h2>
              )}
              {IntroDescription?.value && (
                <div className="text-base animate-top-dga ">
                  <RichText field={IntroDescription} />
                </div>
              )}
            </div>
          </div>
        )}
        {Purpose?.value && (
          <div className="bg-neutral-100">
            <div className="responsive flex flex-col gap-6 richtext-li-custom py-10">
              <RichText field={Purpose} className="animate-top-dga " />
            </div>
          </div>
        )}
        {(PrinciplesTitle?.value || (PrinciplesItems && PrinciplesItems?.length > 0)) && (
          <div className=" responsive flex flex-col gap-8 py-10">
            {PrinciplesTitle?.value && (
              <h2 className="font-semibold text-2xl md:text-3xl md:font-bold animate-top-dga ">
                {PrinciplesTitle?.value}
              </h2>
            )}
            {PrinciplesItems && PrinciplesItems?.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {PrinciplesItems?.map((card: any) => {
                  const { Description, Icon, Title } = card?.fields || {};
                  return Title?.value || Description?.value ? (
                    <div
                      key={card?.id}
                      className="p-4 rounded-3xl border border-neutral-300 flex flex-col gap-6 animate-top-dga "
                    >
                      {Icon?.value?.src && (
                        <div className="relative h-[48px] w-[48px] rounded-full">
                          <Image
                            src={extractRelativePath(Icon?.value?.src)}
                            alt={Icon?.value?.alt?.trim() || "Open data icon"}
                            layout="fill"
                            className="w-full h-full object-contain rounded-full"
                          />
                        </div>
                      )}
                      {(Title?.value || Description?.value) && (
                        <div className="flex flex-col gap-2">
                          {Title?.value && <h3 className="text-lg font-bold">{Title?.value}</h3>}
                          {Description?.value && (
                            <div className="text-base richtext-li-custom">
                              <RichText field={Description} />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <></>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {CTAButtonText?.value && (
          <div className="responsive pb-10">
            <Link
              passHref
              href={
                CTAButtonUrl?.value?.linktype === "external"
                  ? CTAButtonUrl?.value?.href ?? "#"
                  : extractRelativeURLPath(CTAButtonUrl?.value?.href) ?? "#"
              }
            >
              <a
                className={`animate-top-dga ${getDgAButtonClassNames("primary", "lg")}`}
                target={CTAButtonUrl?.value?.target ?? "_self"}
                rel={
                  CTAButtonUrl?.value?.target === "_blank"
                    ? "noopener noreferrer"
                    : undefined
                }
              >
                {CTAButtonText?.value}
              </a>
            </Link>
          </div>
        )}

        <div className="responsive flex flex-col gap-8 pb-10">
          {PrinciplesTitle?.value && (
            <h2 className="font-semibold text-2xl md:text-3xl md:font-bold animate-top-dga">
              {PrinciplesTitle.value}
            </h2>
          )}

          <DGAWarningBox
            label={WarningBoxLabel}
            content={WarningBoxContent}
            HideWarningBox={HideWarningBox}
          />

          <DGACTABox
            title={CTABoxTitle}
            description={CTABoxDescription}
            buttonText={CTABoxButtonText}
            buttonLink={CTABoxButtonLink}
            HideCTABox={HideCTABox}
          />
        </div>
      </div>
    </section>
  );
};

export default withDatasourceCheck()<OpenDataPageContentProps>(OpenDataPageContent);
